@let isAuthorized = isAuthorized$ | async;
@let screenSaverBuffered = this.screenSaverBuffered$ | async;
@let screenSaver = screenSaver$ | async;
@let existingOrganization =
  storedDepartmentTransformed() &&
  storedDepartmentTransformed()?._ownerOrganization?.name
    ? {
        name: storedDepartmentTransformed()?._ownerOrganization?.name!,
        address: storedDepartmentTransformed()?._ownerOrganization?.address,
        emrType:
          organizationEmrType() ||
          storedDepartmentTransformed()?._ownerOrganization?.emrType ||
          null
      }
    : null;

@defer {
  <aiomed-notification />
}

<nz-layout aiomedChargeNotifier class="layout">
  @if (!isPhoneDevice()) {
    @if (
      isUrlNotLoginOrResidentOrInsight() &&
      isAuthorized &&
      !userWithNoDegree() &&
      !(isZoomCallPage$ | async)
    ) {
      <nz-sider
        [class.desktop-forced]="(isForcedResolution$ | async) === false"
        [class.tablet-forced]="(isForcedResolution$ | async) === true"
        [class.tablet]="isMobile"
        class="nz-sider"
        nzTheme="light"
        nzWidth="72px">
        <aiomed-menu
          (collapseMenuEmitter)="
            handlerCollapseMenuEmitter($event)
          "></aiomed-menu>
      </nz-sider>
    }
    <nz-layout
      [class.desktop-forced]="(isForcedResolution$ | async) === false"
      [class.right-layout]="
        isUrlNotLoginOrResidentOrInsight() &&
        isAuthorized &&
        !userWithNoDegree() &&
        !(isZoomCallPage$ | async)
      "
      [class.tablet-forced]="(isForcedResolution$ | async) === true"
      [class.tablet]="isMobile">
      <nz-content>
        <main [class.simulation-mode]="isSimulationMode()">
          @if (isSimulationMode()) {
            <p
              class="simulation-banner"
              [attr.data-cy]="'simulation-mode-banner'">
              <i nz-icon nzType="icons:flask-regular"></i>
              <span>SIMULATION MODE - No real patient data</span>
            </p>
          }
          @if (isInitialLoading() || !isFirstNavigationPerformed()) {
            <div class="initial-loader__wrapper">
              <div class="initial-loader"></div>
            </div>
          }
          <router-outlet></router-outlet>
          <aiomed-pa-onboarding />
          <aiomed-pa-daily-briefing />
        </main>
      </nz-content>
    </nz-layout>
  } @else {
    <section class="notification-access">
      <div class="notification-access__content">
        <img
          #imageToRefetch
          class="logo"
          src="assets/images/AiOmed_logo_small.svg"
          alt="AiOmed_logo" />
        <div class="notification-access__title">
          Mobile Access <br />
          Not Yet Available
        </div>
        <div class="notification-access__text">
          Our system is currently accessible only on
          <strong>desktop devices.</strong> <br />
          Mobile support is coming soon.
          @if (!hasNotificationAccess()) {
            Please use a desktop to view your notification.
          }
        </div>
        <div class="notification-access__bottom-text">
          Have questions?<br />
          support&#64;aiomed.com
        </div>
      </div>
    </section>
  }
</nz-layout>
@if (pccLoginSettingsTabletErrorShown$ | async; as errorShown) {
  <aiomed-warning-modal
    [title]="'LOGIN SETTINGS'"
    [descriptionTemplate]="descriptionTemplate"
    [zIndex]="1001"
    [isLoading]="false"
    [hideSave]="true"
    [closeText]="'OK'"
    [width]="800"
    (closeModal)="hidePccLoginSettingsTabletError()"></aiomed-warning-modal>
  <ng-template #descriptionTemplate>
    <span>
      {{ lastClickedEMRName() }} login is not enabled for your account.
      <strong>Please log in with your AiOmed user first.</strong> Contact your
      administrator to enable {{ lastClickedEMRName() }} login for future use.
    </span>
    <div class="login-settings__error" style="margin-top: 26px">
      <nz-collapse nzGhost>
        <nz-collapse-panel
          nzHeader="Technical details"
          [nzActive]="pccLoginErrorTechnicalDetailsVisible">
          {{ errorShown }}
        </nz-collapse-panel>
      </nz-collapse>
    </div>
  </ng-template>
}
@if (emrLoginSettingsTabletErrorInvitationTimeoutShown(); as errorShown) {
  <aiomed-warning-modal
    [title]="'SOMETHING WENT WRONG'"
    [description]="errorShown"
    [zIndex]="1001"
    [isLoading]="false"
    [saveText]="'Log in'"
    [closeText]="'Leave'"
    [width]="527"
    (closeModal)="
      pccLoginWasNotPerformedButClosed();
      setEmrLoginSettingsTabletErrorInvitationTimeoutShown()
    "
    (saveModalDataEmitter)="
      handlerLoginWithPcc();
      setEmrLoginSettingsTabletErrorInvitationTimeoutShown()
    " />
}
@if (loginFailedError(); as loginFailedError) {
  <aiomed-warning-modal
    [isMobile]="isMobile"
    [title]="'LOG IN FAILED'"
    [description]="
      'A system error occurred. Clicking the button below will automatically notify our support team with error code ' +
      loginFailedError.errorCode +
      '.'
    "
    [zIndex]="1001"
    [isLoading]="false"
    [saveText]="'Notify Support'"
    [closeText]="'Close'"
    [width]="800"
    (closeModal)="closeLoginFailedError()"
    iconType="aio:warning-circle-regular"
    (saveModalDataEmitter)="notifySupportAboutLoginFailure()" />
}
@if ((pccLoginWasNotPerformedButClosed$ | async) && !(isOffline$ | async)) {
  <aiomed-warning-modal
    [isMobile]="isMobile"
    [title]="'EMR LOG IN REQUIRED'"
    [description]="'To report to PointClickCare, you must first log in. Please log in to perform actions.'"
    [zIndex]="1001"
    [isLoading]="false"
    [saveText]="'Log in'"
    [closeText]="'Close'"
    [width]="595"
    (closeModal)="pccLoginWasNotPerformedButClosed()"
    (saveModalDataEmitter)="handlerLoginWithPcc()"></aiomed-warning-modal>
  <ng-template #descriptionTemplate>
    <span
      >PointClickCare login is not enabled for your account.
      <strong>Please log in with your AiOmed user first.</strong> Contact your
      administrator to enable PointClickCare login for future use.</span
    >
  </ng-template>
}
@if (pccLoginWasPerformedWithCNAUser$ | async) {
  <aiomed-warning-modal
    [title]="'ACCESS LIMITED TO AUTHORISED ACCOUNTS'"
    [isMobile]="isMobile"
    description="We're sorry, but only users classified as 'Nurse' can access our system via PCC. If you believe this is an error or need assistance with your account, please contact your administrator for support"
    [zIndex]="1001"
    [isLoading]="false"
    [hideSave]="true"
    [closeText]="'Close'"
    [width]="isMobile ? 800 : 863"
    (closeModal)="pccLoginWasPerformedWithCNAUserClose()" />
}

@if (nonDegreeUserSelectedCNA()) {
  <aiomed-warning-modal
    [title]="'ACCESS RESTRICTED'"
    [isMobile]="true"
    [backdropHeightLimited]="false"
    description="The desktop system is not accessible for CNA accounts"
    [zIndex]="1002"
    [isLoading]="false"
    [hideSave]="true"
    [closeText]="'OK'"
    [width]="463"
    (closeModal)="setNonDegreeUserSelectedCNA()" />
}

@if (
  (isPopupAboutWaitingWiFiToBeResumedShown() ||
    isOrganizationsLoadingFailedAndOffline() ||
    isUserLoadingFailedAndOffline() ||
    isSelectNetworkPopupShown()) &&
  isMobile
) {
  <aiomed-warning-modal
    (closeModal)="selectNetwork()"
    [isMobile]="true"
    [title]="'CONNECT TO WI-FI'"
    [description]="'Select wi-fi network to continue.'"
    [zIndex]="1001"
    [closeText]="'Choose network'"
    [hideSave]="true"
    [width]="650"
    [isNormalTitle]="true"
    [iconTemplate]="offlineIcon"
    [iconBackground]="'var(--primary-dark)'"
    [attr.data-cy]="'poor-wifi-banner'" />
}

<ng-template #offlineIcon>
  <span class="offline-icon">
    <svg
      width="56"
      height="56"
      viewBox="0 0 28 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.2501 21.5C15.2501 21.7472 15.1768 21.9889 15.0394 22.1945C14.9021 22.4 14.7068 22.5603 14.4784 22.6549C14.25 22.7495 13.9987 22.7742 13.7562 22.726C13.5137 22.6778 13.291 22.5587 13.1162 22.3839C12.9414 22.2091 12.8223 21.9864 12.7741 21.7439C12.7259 21.5014 12.7506 21.2501 12.8452 21.0217C12.9398 20.7933 13.1 20.598 13.3056 20.4607C13.5112 20.3233 13.7528 20.25 14.0001 20.25C14.3316 20.25 14.6495 20.3817 14.884 20.6161C15.1184 20.8505 15.2501 21.1685 15.2501 21.5ZM4.55507 0.500013C4.41777 0.372571 4.23774 0.301126 4.05041 0.299734C3.86309 0.298342 3.68202 0.367105 3.54284 0.492492C3.40366 0.61788 3.31644 0.790814 3.29835 0.97727C3.28026 1.16373 3.33259 1.3502 3.44507 1.50001L5.66632 3.94751C3.81213 4.73907 2.08049 5.79136 0.523817 7.07251C0.44725 7.1349 0.383758 7.21179 0.336987 7.29878C0.290216 7.38577 0.261087 7.48114 0.251272 7.57941C0.241456 7.67769 0.251148 7.77694 0.27979 7.87146C0.308433 7.96598 0.355462 8.05391 0.418178 8.1302C0.480894 8.2065 0.558062 8.26966 0.645252 8.31605C0.732442 8.36245 0.827936 8.39116 0.926254 8.40055C1.02457 8.40994 1.12377 8.39982 1.21817 8.37077C1.31257 8.34172 1.40029 8.29431 1.47632 8.23126C3.05821 6.92839 4.83374 5.88037 6.73882 5.12501L10.0488 8.76626C8.04088 9.3059 6.16423 10.2498 4.53382 11.54C4.37784 11.6637 4.27737 11.8442 4.25451 12.042C4.23166 12.2397 4.28829 12.4384 4.41194 12.5944C4.5356 12.7504 4.71615 12.8508 4.91389 12.8737C5.11162 12.8965 5.31034 12.8399 5.46632 12.7163C7.14281 11.3879 9.10609 10.4689 11.2001 10.0325L15.0913 14.3138C14.7291 14.2711 14.3648 14.2498 14.0001 14.25C12.0442 14.2449 10.1378 14.8646 8.55882 16.0188C8.43087 16.1119 8.33569 16.2432 8.28689 16.3938C8.23809 16.5444 8.23819 16.7065 8.28716 16.8571C8.33613 17.0076 8.43147 17.1387 8.55952 17.2318C8.68758 17.3248 8.84178 17.375 9.00007 17.375C9.15875 17.3756 9.31341 17.3252 9.44132 17.2313C10.5063 16.4569 11.751 15.9667 13.058 15.8068C14.3649 15.6469 15.6911 15.8227 16.9113 16.3175L23.4451 23.5C23.579 23.6472 23.7659 23.7352 23.9647 23.7445C24.1635 23.7539 24.3579 23.6839 24.5051 23.55C24.6523 23.4161 24.7402 23.2292 24.7496 23.0304C24.759 22.8316 24.689 22.6372 24.5551 22.49L4.55507 0.500013ZM27.4763 7.06876C23.6749 3.95553 18.9136 2.25303 14.0001 2.25001C13.0907 2.25041 12.1822 2.30803 11.2801 2.42251C11.1806 2.43284 11.0843 2.46295 10.9966 2.51107C10.909 2.55918 10.8319 2.62434 10.7698 2.70269C10.7077 2.78105 10.6619 2.87103 10.6351 2.96734C10.6082 3.06365 10.601 3.16435 10.6136 3.26352C10.6263 3.36269 10.6587 3.45833 10.7088 3.54481C10.759 3.63129 10.8259 3.70687 10.9057 3.7671C10.9855 3.82733 11.0765 3.871 11.1735 3.89555C11.2704 3.92009 11.3712 3.92501 11.4701 3.91001C12.3092 3.80342 13.1542 3.74999 14.0001 3.75001C18.5663 3.75241 22.9911 5.33437 26.5238 8.22751C26.5998 8.29056 26.6876 8.33797 26.782 8.36702C26.8764 8.39607 26.9756 8.40619 27.0739 8.3968C27.1722 8.38741 27.2677 8.3587 27.3549 8.3123C27.4421 8.26591 27.5192 8.20275 27.582 8.12645C27.6447 8.05016 27.6917 7.96223 27.7203 7.86771C27.749 7.77319 27.7587 7.67394 27.7489 7.57566C27.739 7.47739 27.7099 7.38202 27.6631 7.29503C27.6164 7.20804 27.5529 7.13115 27.4763 7.06876ZM22.5338 12.7188C22.6673 12.8218 22.8315 12.8768 23.0001 12.875C23.1557 12.875 23.3076 12.8266 23.4345 12.7364C23.5614 12.6463 23.6571 12.5189 23.7083 12.3719C23.7596 12.2249 23.7638 12.0656 23.7204 11.9161C23.6771 11.7666 23.5883 11.6343 23.4663 11.5375C21.6053 10.0649 19.427 9.04589 17.1038 8.56126C17.0068 8.53966 16.9064 8.53758 16.8085 8.55513C16.7106 8.57269 16.6172 8.60954 16.5337 8.66353C16.4502 8.71752 16.3782 8.78758 16.3221 8.86963C16.2659 8.95168 16.2266 9.04408 16.2064 9.14146C16.1863 9.23884 16.1857 9.33925 16.2047 9.43686C16.2238 9.53446 16.262 9.62731 16.3173 9.70999C16.3725 9.79268 16.4436 9.86355 16.5265 9.91849C16.6094 9.97343 16.7024 10.0113 16.8001 10.03C18.8945 10.4679 20.8578 11.3886 22.5338 12.7188Z"
        fill="currentColor" />
    </svg>
  </span>
</ng-template>

@let noRoleSelectedShown = !!userWithNoDegree();
@if (noRoleSelectedShown && isOnline()) {
  <aiomed-no-role-selected
    [isMatrixCare]="
      (!existingOrganization?.emrType && !organizationEmrType()) ||
      existingOrganization?.emrType === OrganizationEmrType.MatrixCare ||
      organizationEmrType() === OrganizationEmrType.MatrixCare
    "
    [isVisible]="noRoleSelectedShown"
    [hasYesDisabled]="hasCNAAccessToken()"
    (yesClicked)="setDegreeCNA()"
    (noClicked)="setUserDegree(false)" />
}

<aiomed-report-loader
  [isDark]="true"
  [loader]="isLoaderShown()"
  [isMobile]="isMobile" />

@if (incorrectTimeSystem$ | async) {
  <aiomed-clock-update-modal
    (closeModal)="
      handlerCloseClockUpdateModal($event)
    "></aiomed-clock-update-modal>
}

@if (userShouldWatchDisclaimer()) {
  <aiomed-sign-disclaimer
    [isVisible]="!(isOffline$ | async) && userShouldWatchDisclaimer()"
    [isMobile]="isMobile"
    [patientLabel]="patientLabels().singular"
    [patientLabelPlural]="patientLabels().plural"
    (decline)="declineDisclaimerLogout()"
    (accept)="acceptDisclaimer()" />
}

@let isBatteryRunningLowScreenShownAndAuthenticated =
  this.isBatteryRunningLowScreenShownAndAuthenticated();
@let isBatteryRunningLowScreenShownAndNotAuthenticated =
  this.isBatteryRunningLowScreenShownAndNotAuthenticated();
@let isBatteryNoPowerAndConnectScreenShown =
  this.isBatteryNoPowerAndConnectScreenShown();
@let isBatteryCriticallyLowScreenShown =
  this.isBatteryCriticallyLowScreenShown();
@let isBatteryTooLowToRunScreenShown = this.isBatteryTooLowToRunScreenShown();
@let isBatteryConnectToPowerAndShuttingDownScreenShown =
  this.isBatteryConnectToPowerAndShuttingDownScreenShown();

@defer (when isMobile) {
  @if (hasChargerJustConnected()) {
    <aiomed-charger-connected />
  }

  @if (
    isBatteryNoPowerAndConnectScreenShown ||
    isBatteryTooLowToRunScreenShown ||
    isBatteryConnectToPowerAndShuttingDownScreenShown
  ) {
    <aiomed-low-charging
      [isChargingScreenShown]="isBatteryTooLowToRunScreenShown"
      [isPoweringScreenShown]="isBatteryNoPowerAndConnectScreenShown"
      [isBatteryConnectToPowerAndShuttingDownScreenShown]="
        isBatteryConnectToPowerAndShuttingDownScreenShown
      "
      [isOnline]="!!isOnline()"
      [batteryLevel]="deviceBatteryLevel()"
      [hasVirtualKeyboard]="hasVirtualKeyboard()"
      (pinCodeCorrectlyEntered)="hideChargingPoweringScreen()"
      (openWiFiNetworkSettings)="selectNetwork()"
      [isDeviceCharging]="isDeviceCharging()" />
  } @else if (
    isBatteryRunningLowScreenShownAndAuthenticated ||
    isBatteryRunningLowScreenShownAndNotAuthenticated ||
    isBatteryCriticallyLowScreenShown
  ) {
    @let title =
      isBatteryCriticallyLowScreenShown
        ? 'Battery critically Low!'
        : 'Battery running low!';
    @let description =
      'You can keep using the tablet, but please charge it soon.';
    @let buttonText =
      isBatteryRunningLowScreenShownAndNotAuthenticated
        ? 'Use now, charge later'
        : isBatteryCriticallyLowScreenShown
          ? 'Thanks, I’ll plug it in'
          : 'OK I’ll charge later';
    <ng-template #isBatteryCriticallyLowScreenShownDescriptionTemplate>
      <p>
        The tablet will shut down in {{ minutesTillShutDown() }} minutes. <br />
        Please plug it in to continue using.
      </p>
    </ng-template>
    <aiomed-half-screen-modal
      [title]="title"
      [buttonText]="buttonText"
      [description]="description"
      [descriptionTemplate]="
        isBatteryCriticallyLowScreenShown
          ? isBatteryCriticallyLowScreenShownDescriptionTemplate
          : null
      "
      (close)="closeBatteryLowScreenPopup()" />
  } @else {
    @if ((isSessionExpire$ | async) && !(isOffline$ | async)) {
      <aiomed-warning-modal
        [isSessionExpired]="true"
        [isMobile]="isMobile"
        [width]="595"
        [zIndex]="1010"
        [hideSave]="true"
        [shouldAutoHide]="true"
        (closeModal)="hideModal()"
        (saveModalDataEmitter)="hideModal()"
        closeText="OK"
        description="You've been logged out due to inactivity. Please log in again to continue"
        title="YOU’VE BEEN LOGGED OUT"></aiomed-warning-modal>
    }

    @if (screenSaver && !isSession()) {
      <aiomed-screen-saver-modal
        [isAuthenticated]="isAuthorized"
        [isMobile]="isMobile"
        [hasVirtualKeyboard]="hasVirtualKeyboard()"
        [screenBlockedStatus]="screenBlockedStatus$ | async"
        [isBuffered]="screenSaverBuffered"
        (submitEmitter)="handlerScreenSaverSubmitEmitter()" />
    }
  }
}

@if (screenSaver && !isSession() && !this.isMobile) {
  <aiomed-screen-saver-modal
    [isAuthenticated]="isAuthorized"
    [isMobile]="isMobile"
    [hasVirtualKeyboard]="hasVirtualKeyboard()"
    [screenBlockedStatus]="screenBlockedStatus$ | async"
    [isBuffered]="screenSaverBuffered"
    (submitEmitter)="handlerScreenSaverSubmitEmitter()" />
}

@if (!isBrowserSupported && !isMobile) {
  <aiomed-unsupported-browsers />
}

@let isSystemAboutToRestartPopupShown = this.isSystemAboutToRestartPopupShown();
@defer (when isMobile) {
  @if (isSystemAboutToRestartPopupShown) {
    <aiomed-notify-before-restart
      [isVisible]="isSystemAboutToRestartPopupShown"
      [secondsBeforeRestart]="secondsBeforeRestart"
      (delayRestart)="restartSystemInTenMinutes()"
      (restartNow)="restartSystemNow()" />
  }
}

@let backendServicesStatus = this.backendServicesStatus();
@defer {
  @if (backendServicesStatus === 'ERROR') {
    <aiomed-system-status-modal
      [isVisible]="backendServicesStatus === 'ERROR'" />
  }
}

<aiomed-tour-host />

<aiomed-tour-host />
